import React, {useEffect, useState, useRef} from "react";
import { PieChart2, DynamicSankeyChart, HorizontalBarMetrics, TrendsLineWithMetrics } from "../../layouts/ChartLayout";
import { FaTimes } from "react-icons/fa";
import server from "../../../api/server";
import "../../../dashboard-components/loader.css"
import CustomerRecords from "../customers/CustomerRecords.jsx";
import ModernTimeDropdown from "../../layouts/modernDropdown";
import ModernDateRangePicker from "../../layouts/ModernDateRangePicker";
import { FaFilter } from "react-icons/fa";
import CustomerAdvancedFiltersPanel from "../../layouts/CustomerAdvancedFiltersPanel";
import { useNavigate, useLocation } from 'react-router-dom';
import {
  initializeFiltersFromUrl,
  updateUrlWithFilters,
  extractModuleParameterFiltersFromFlatFilters,
  normalizeInteractionFiltersFromUrl,
  getCrmLeadCreatedAtApiArgs,
} from '../../../utils/filterUtils';
import CrmLeadCreatedAtFilterChips, { shouldHideCrmLeadCreatedAtChipKey } from "../../layouts/CrmLeadCreatedAtFilterChips";
import { Download } from "lucide-react";
import { downloadRowsAsWorkbook, downloadWorkbook } from "../../../utils/chartDownloadUtils";
import {
  buildAdditionalTagFilterConditions,
  getAdditionalTagFilterValues,
} from "../../../utils/filterPayloadBuilder";

const toSnakeCase = (value) => String(value || '').trim().replace(/([A-Z])/g, '_$1').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '').toLowerCase();
const normalizeKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
const asValidBetween = (value) => {
  if (!value || typeof value !== 'object') return null;
  const lowerRaw = value.lower;
  const upperRaw = value.upper;
  const lower = lowerRaw === '' || lowerRaw === null || lowerRaw === undefined ? null : Number(lowerRaw);
  const upper = upperRaw === '' || upperRaw === null || upperRaw === undefined ? null : Number(upperRaw);
  const hasLower = lower !== null && !Number.isNaN(lower);
  const hasUpper = upper !== null && !Number.isNaN(upper);
  if (!hasLower && !hasUpper) return null;
  return { lower: hasLower ? lower : null, upper: hasUpper ? upper : null };
};
const hasMeaningfulValue = (value) => {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return asValidBetween(value) !== null;
  return value !== '';
};
const buildModuleParameterFilters = (filters = {}, advancedFilters = {}) => {
  const output = [
    ...extractModuleParameterFiltersFromFlatFilters(filters),
    ...buildAdditionalTagFilterConditions(advancedFilters),
  ];
  if (Array.isArray(advancedFilters.business_insights_multiple)) {
    advancedFilters.business_insights_multiple.forEach((item) => {
      const field = toSnakeCase(item?.business_insights_parameter);
      const value = item?.business_insights_parameter_value;
      const betweenValue = asValidBetween(value);
      if (field && betweenValue) {
        output.push({ field, operator: 'between', value: betweenValue, module: 'business_insight' });
      } else if (field && hasMeaningfulValue(value)) {
        output.push({ field, operator: Array.isArray(value) ? 'in' : 'eq', value, module: 'business_insight' });
      }
    });
  }
  if (advancedFilters.custom_module_parameter_name && hasMeaningfulValue(advancedFilters.custom_module_parameter_answer)) {
    const betweenValue = asValidBetween(advancedFilters.custom_module_parameter_answer);
    output.push({
      field: toSnakeCase(advancedFilters.custom_module_parameter_name),
      operator: betweenValue ? 'between' : Array.isArray(advancedFilters.custom_module_parameter_answer) ? 'in' : 'eq',
      value: betweenValue || advancedFilters.custom_module_parameter_answer,
      module: 'custom_module'
    });
  }
  if (Array.isArray(advancedFilters.custom_module_multiple)) {
    advancedFilters.custom_module_multiple.forEach((item) => {
      const field = toSnakeCase(item?.custom_module_parameter_name);
      const value = item?.custom_module_parameter_answer;
      const betweenValue = asValidBetween(value);
      if (field && betweenValue) {
        output.push({ field, operator: 'between', value: betweenValue, module: 'custom_module' });
      } else if (field && hasMeaningfulValue(value)) {
        output.push({ field, operator: Array.isArray(value) ? 'in' : 'eq', value, module: 'custom_module' });
      }
    });
  }
  const dedupe = [];
  const seen = new Set();
  output.forEach((item) => {
    const key = JSON.stringify([item.field, item.operator, item.module, item.value]);
    if (seen.has(key)) return;
    seen.add(key);
    dedupe.push(item);
  });
  return dedupe;
};
const buildParameterQueryFilters = (conditions = []) => {
  const query = {};
  conditions.forEach((condition) => {
    if (!condition.module) return;
    const key = condition.module ? `${condition.module}_${condition.field}` : condition.field;
    if (condition.operator === 'between' && condition.value && typeof condition.value === 'object') {
      const lowerKey = `${key}_lower`;
      const upperKey = `${key}_upper`;
      if (condition.value.lower !== undefined && condition.value.lower !== null && condition.value.lower !== '') {
        query[lowerKey] = condition.value.lower;
      }
      if (condition.value.upper !== undefined && condition.value.upper !== null && condition.value.upper !== '') {
        query[upperKey] = condition.value.upper;
      }
      return;
    }
    if (condition.value !== undefined && condition.value !== null && condition.value !== '') {
      query[key] = condition.value;
    }
  });
  return query;
};

const BusinessInsights = ({serviceID, clientID}) => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Default filter values
    const defaultFilters = {
      employee: '',
      team_name: '',
      firstinteraction: '',
      firstinteraction_range_type: 'between',
      lastinteraction: 'Last 7 Days',
      lastinteraction_range_type: 'between',
      'First Interaction Start': '',
      'First Interaction End': '',
      'Last Interaction Start': '',
      'Last Interaction End': '',
    };
    const defaultAdvancedFilters = {
      refusalCategory: '',
      customerPotential: "",
      customer_outcome: '',
      openConcerns: '',
      id: '',
      interactions: '',
      business_insights_multiple: [],
      concernCategory: '',
      concernSubcategory: '',
      queryCategory: '',
      querySubcategory: '',
      disposition: '',
      subdisposition: '',
      lead_source: '',
      primary_language: '',
      secondary_languages: '',
      lead_sub_source: '',
      tag: '',
      tag2: '',
      tag3: '',
      tag4: '',
      tag5: '',
      tag6: '',
      tag7: '',
      tag8: '',
      tag9: '',
      tag10: '',
      tag11: '',
      tag12: '',
      tag13: '',
      tag14: '',
      tag15: '',
      tag16: '',
      tag17: '',
      tag18: '',
      tag19: '',
      tag20: '',
      tag21: '',
      tag22: '',
      tag23: '',
      tag24: '',
      tag25: '',
      tag26: '',
      tag27: '',
      tag28: '',
      tag29: '',
      tag30: '',
      crm_lead_stage: '',
      crm_lead_sub_stage: '',
      customer_phone_number: '',
      crm_location: '',
      campaign_id: '',
      crm_lead_created_at: '',
      crm_lead_created_at_start: '',
      crm_lead_created_at_end: '',
      crm_lead_created_at_range_type: 'between',
      custom_module_multiple: [],
      custom_module_parameter_name: '',
      custom_module_parameter_answer: ''
    }

    // Initialize filters from URL or use defaults
    const initialFilterState = initializeFiltersFromUrl(location, defaultFilters, defaultAdvancedFilters);

    const [service_id, setService_id] = useState(null)
    const [client_id, setClient_id] = useState(null)
    const [email, setEmail] = useState(null)
    const [showAdvancedFilterPanel, setShowAdvancedFilterPanel] = useState(false);
    // legacy global loader (can be removed later)
    const [isLoading, setIsLoading] = useState(false)
    // granular loading flags
    const [loadingDisposition, setLoadingDisposition] = useState(true);
    const [loadingPieDist, setLoadingPieDist] = useState(true);
    const [loadingLeadQuality, setLoadingLeadQuality] = useState(true);

    // debounce refs
    const filtersDebounceRef = useRef(null);
    const firstFiltersLoadRef = useRef(true);
    const [selectedSegment, setSelectedSegment] = useState(null)
    const [dispositionData, setDispositionData] = useState(null)
    const [pieDistribution, setPieDistribution] = useState(null)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [leadQualityData, setLeadQualityData] = useState([])
  const [selectedAnalyticsTab, setSelectedAnalyticsTab] = useState('custom_module') // 'business' | 'custom_module' | 'first_response_time' | 'conversation_disposition' | 'lead_quality'
  const [loadingCustomModule, setLoadingCustomModule] = useState(false)
  const [customModuleCharts, setCustomModuleCharts] = useState([]) // [{ title, data: [{name, value}] }]
  const [loadingFirstResponseTime, setLoadingFirstResponseTime] = useState(false)
  const [firstResponseAgentWiseData, setFirstResponseAgentWiseData] = useState([])
  const [firstResponseDailyTrendData, setFirstResponseDailyTrendData] = useState([])
  const [firstResponseAgentMetrics, setFirstResponseAgentMetrics] = useState([])
  const [firstResponseTrendMetrics, setFirstResponseTrendMetrics] = useState([])
  const [activeFirstResponseAgentMetric, setActiveFirstResponseAgentMetric] = useState('frt')
  const [activeFirstResponseTrendMetric, setActiveFirstResponseTrendMetric] = useState('frt')
    const [expandedSections, setExpandedSections] = useState({
      businessInsights: false,
      concernsAndQueries: false,
      dispositionAndSubdisposition: false,
      customModule: false
    });
    const colors = [
      "#10B981", // green
      "#3B82F6", // blue
      "#F59E0B", // amber
      "#F35858", // red
      "#84CC16", // lime
      "#956BF8", // violet
      "#06B6D4", // cyan
      "#EC4899", // pink
      "#0EA5E9", // sky-500 (bright sky blue)
      "#EAB308", // yellow
      "#F97316" // orange
    ];
    
    const buildFiltersFromUrl = (filterState = initialFilterState) =>
      normalizeInteractionFiltersFromUrl(
        {
          ...defaultFilters,
          ...filterState.filters,
          firstinteraction_range_type: filterState.filters.firstinteraction_range_type || 'between',
          lastinteraction_range_type: filterState.filters.lastinteraction_range_type || 'between',
        },
        { customStart: filterState.customStart, customEnd: filterState.customEnd }
      );

    const [filters, setFilters] = useState(() => buildFiltersFromUrl());
    const [advancedFilters, setAdvancedFilters] = useState(initialFilterState.advancedFilters);
    const [multiSelectFields, setMultiSelectFields] = useState([]);
    const isMultiSelectField = (key) => Array.isArray(multiSelectFields) && multiSelectFields.includes(key);

    // Update URL whenever filters change
    useEffect(() => {
        if (!client_id || !service_id) return;
        
        // Debounce URL updates to avoid conflicts with navigation
        const timeoutId = setTimeout(() => {
            const moduleParameterFilters = buildModuleParameterFilters(filters, advancedFilters);
            const queryParameterFilters = buildParameterQueryFilters(moduleParameterFilters);
            const advancedFiltersForUrl = { ...advancedFilters };
            delete advancedFiltersForUrl.business_insights_multiple;
            delete advancedFiltersForUrl.custom_module_multiple;
            delete advancedFiltersForUrl.custom_module_parameter_name;
            delete advancedFiltersForUrl.custom_module_parameter_answer;
            updateUrlWithFilters(navigate, location, { ...filters, ...queryParameterFilters }, advancedFiltersForUrl);
        }, 300);
        
        return () => clearTimeout(timeoutId);
    }, [filters, advancedFilters, client_id, service_id]);

    const hydratedFromUrlRef = useRef(location.search);
    useEffect(() => {
      if (!client_id || !service_id) return;
      if (hydratedFromUrlRef.current === location.search) return;
      hydratedFromUrlRef.current = location.search;
      const nextState = initializeFiltersFromUrl(location, defaultFilters, defaultAdvancedFilters);
      setFilters(buildFiltersFromUrl(nextState));
      setAdvancedFilters(nextState.advancedFilters);
    }, [location.search, client_id, service_id]);

    const handleFilterChange = (e, key) => {
      const rawValue = e.target.value;
      let value = isMultiSelectField(key)
        ? (Array.isArray(rawValue) ? rawValue : (rawValue ? [rawValue] : []))
        : rawValue;
      if (key === 'firstinteraction' && !Array.isArray(value) && value !== 'Custom Range') {
        setFilters((prev) => ({
          ...prev,
          firstinteraction: value,
          firstinteraction_range_type: 'between',
          'First Interaction Start': '',
          'First Interaction End': '',
        }));
      } else if (key === 'lastinteraction' && !Array.isArray(value) && value !== 'Custom Range') {
        setFilters((prev) => ({
          ...prev,
          lastinteraction: value,
          lastinteraction_range_type: 'between',
          'Last Interaction Start': '',
          'Last Interaction End': '',
        }));
      } else {
        setFilters((prev) => ({ ...prev, [key]: value }));
      }
    };
    const updateBusinessInsights = (index, field, value) => {
      setAdvancedFilters((prev) => ({
        ...prev,
        business_insights_multiple: prev.business_insights_multiple.map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        )
      }));
    };
    
    const [dummyFilterData, setDummyFilterData] = useState({
      employees: [],
      modes: ['Call', 'Email', 'Chat'],
      firstinteraction: ['Today',
        'Yesterday',
        'This Week',
        "This Month",
        "Last Month",
        "Last 7 Days",
        "Last 30 Days",
        "Last 3 Months",
        "Custom Range"],
      lastinteraction: ['Today',
        'Yesterday',
        'This Week',
        "This Month",
        "Last Month",
        "Last 7 Days",
        "Last 30 Days",
        "Last 3 Months",
        "Custom Range"]
    });
    let [dummyAdvancedFilterData, setDummyAdvancedFilterData] =useState({
      refusal_category: [],
      open_concerns_and_queries: ['Yes', 'No'],
      customer_outcome: [],
      customer_potential: [],
      business_insight_parameters: [],
      concern_categories: [],
      concern_subcategories: [],
      query_categories: [],
      query_subcategories: [],
      disposition: [],
      subdisposition: [],
      lead_sources: [],
      primary_languages: [],
      secondary_languages: [],
      lead_sub_sources: [],
      campaign_ids: [],
      tags: [],
      tags2: [],
      tags3: [],
      tags4: [],
      tags5: [],
      tags6: [],
      tags7: [],
      tags8: [],
      tags9: [],
      tags10: [],
      tags11: [],
      tags12: [],
      tags13: [],
      tags14: [],
      tags15: [],
      tags16: [],
      tags17: [],
      tags18: [],
      tags19: [],
      tags20: [],
      tags21: [],
      tags22: [],
      tags23: [],
      tags24: [],
      tags25: [],
      tags26: [],
      tags27: [],
      tags28: [],
      tags29: [],
      tags30: [],
      lead_stages: [],
      lead_sub_stages: [],
      custom_module: []
    })
    const [filterDetailsLoaded, setFilterDetailsLoaded] = useState(false);
    const [role] = useState(()=> {
      const userCookie = getCookie('dashboard_login_user_creds');
      if (userCookie) {
        const userData = JSON.parse(userCookie);
        const role = userData.role
        
        // If 'TRADEMARK' is found, return it; otherwise, return the first service_id found or 'DEFAULT'
        return role
      }
      return 'admin';
    })
  const metricsAbortRef = useRef(null);
  const pieAbortRef = useRef(null);
  const customModuleAbortRef = useRef(null);
  const firstResponseTimeAbortRef = useRef(null);

  const getMetrics = () => {
    if(metricsAbortRef.current){ metricsAbortRef.current.abort(); }
    const controller = new AbortController();
    metricsAbortRef.current = controller;
    // reset granular loaders
    setLoadingDisposition(true);
    setLoadingLeadQuality(true);
    const moduleParameterFilters = buildModuleParameterFilters(filters, advancedFilters);

    // ---- Disposition Sankey ----
    server.getDispositionDistribution(client_id, service_id, email, 'customer-insights', undefined, advancedFilters.id, advancedFilters.customerPotential, advancedFilters.interactions, advancedFilters.openConcerns, filters.employee, filters.lastinteraction, filters.firstinteraction, filters['Last Interaction Start'], filters['Last Interaction End'], filters['First Interaction Start'], filters['First Interaction End'], filters.record_type, filters.team_name, advancedFilters.business_insights_multiple, advancedFilters.concernCategory, advancedFilters.concernSubcategory, advancedFilters.queryCategory, advancedFilters.querySubcategory, advancedFilters.disposition, advancedFilters.subdisposition, advancedFilters.lead_source, advancedFilters.primary_language, advancedFilters.secondary_languages, advancedFilters.lead_sub_source, advancedFilters.tag, advancedFilters.tag2, advancedFilters.tag3, ...getAdditionalTagFilterValues(advancedFilters), advancedFilters.crm_lead_stage, advancedFilters.crm_lead_sub_stage, advancedFilters.customer_phone_number, advancedFilters.crm_location, advancedFilters.campaign_id, advancedFilters.custom_module_parameter_name, advancedFilters.custom_module_parameter_answer, controller.signal, moduleParameterFilters, ...getCrmLeadCreatedAtApiArgs(advancedFilters))
      .then(async res1 => {
        if (res1.status === 200) {
          const json = await res1.json();
          if (json.data) setDispositionData(json.data);
        }
      })
      .catch((err) => {
        if (err?.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.error('Disposition distribution failed', err);
        }
      })
      .finally(()=> setLoadingDisposition(false));

    // ---- Lead Quality ----
    server.getLeadQualityData(client_id, service_id, email, 'customer-insights', undefined, advancedFilters.id, advancedFilters.customerPotential, advancedFilters.interactions, advancedFilters.openConcerns, filters.employee, filters.lastinteraction, filters.firstinteraction, filters['Last Interaction Start'], filters['Last Interaction End'], filters['First Interaction Start'], filters['First Interaction End'], advancedFilters.business_insights_multiple, advancedFilters.concernCategory, advancedFilters.concernSubcategory, advancedFilters.queryCategory, advancedFilters.querySubcategory, advancedFilters.disposition, advancedFilters.subdisposition, advancedFilters.lead_source, advancedFilters.primary_language, advancedFilters.secondary_languages, advancedFilters.lead_sub_source, advancedFilters.tag, advancedFilters.tag2, advancedFilters.tag3, ...getAdditionalTagFilterValues(advancedFilters), advancedFilters.crm_lead_stage, advancedFilters.crm_lead_sub_stage, advancedFilters.customer_phone_number, advancedFilters.crm_location, advancedFilters.campaign_id, advancedFilters.custom_module_parameter_name, advancedFilters.custom_module_parameter_answer, controller.signal, moduleParameterFilters, ...getCrmLeadCreatedAtApiArgs(advancedFilters), filters.team_name)
      .then(async res4 => {
        if (res4.status === 200) {
          const json = await res4.json();
          if (json.data) setLeadQualityData(json.data);
        }
      })
      .catch((err) => {
        if (err?.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.error('Lead quality fetch failed', err);
        }
      })
      .finally(()=> setLoadingLeadQuality(false));

  }
  const getBusinessInsightPie = () => {
    if (pieAbortRef.current) { pieAbortRef.current.abort(); }
    const controller = new AbortController();
    pieAbortRef.current = controller;
    setLoadingPieDist(true);
    const moduleParameterFilters = buildModuleParameterFilters(filters, advancedFilters);
    server.getPieDistribution(
      client_id, service_id, email, 'customer-insights', undefined,
      advancedFilters.id, advancedFilters.customerPotential, advancedFilters.interactions, advancedFilters.openConcerns,
      filters.employee, filters.lastinteraction, filters.firstinteraction,
      filters['Last Interaction Start'], filters['Last Interaction End'],
      filters['First Interaction Start'], filters['First Interaction End'],
      advancedFilters.business_insights_multiple,
      advancedFilters.concernCategory, advancedFilters.concernSubcategory,
      advancedFilters.queryCategory, advancedFilters.querySubcategory,
      advancedFilters.disposition, advancedFilters.subdisposition,
      advancedFilters.lead_source, advancedFilters.primary_language, advancedFilters.secondary_languages, advancedFilters.lead_sub_source, advancedFilters.tag, advancedFilters.tag2, advancedFilters.tag3, ...getAdditionalTagFilterValues(advancedFilters),
      advancedFilters.crm_lead_stage, advancedFilters.crm_lead_sub_stage, advancedFilters.customer_phone_number, advancedFilters.crm_location,
      advancedFilters.campaign_id,
      advancedFilters.custom_module_parameter_name, advancedFilters.custom_module_parameter_answer,
      controller.signal,
      moduleParameterFilters,
      ...getCrmLeadCreatedAtApiArgs(advancedFilters), filters.team_name
    ).then(async res => {
      if (res.status === 200) {
        const json = await res.json();
        if (json.data) setPieDistribution(json.data);
      }
    })
    .catch((err) => {
      if (err?.name !== 'AbortError') {
        // eslint-disable-next-line no-console
        console.error('Pie distribution fetch failed', err);
      }
    })
    .finally(()=> setLoadingPieDist(false));
  }
  const getCustomModuleAnalytics = () => {
    if(customModuleAbortRef.current){ customModuleAbortRef.current.abort(); }
    const controller = new AbortController();
    customModuleAbortRef.current = controller;
    setLoadingCustomModule(true);
    const moduleParameterFilters = buildModuleParameterFilters(filters, advancedFilters);
    server.getCustomModuleAnalytics(
      client_id, service_id, email, 'customer-insights', undefined,
      advancedFilters.id, advancedFilters.customerPotential, advancedFilters.interactions, advancedFilters.openConcerns,
      filters.employee, filters.lastinteraction, filters.firstinteraction,
      filters['Last Interaction Start'], filters['Last Interaction End'],
      filters['First Interaction Start'], filters['First Interaction End'],
      advancedFilters.business_insights_multiple, advancedFilters.concernCategory, advancedFilters.concernSubcategory, advancedFilters.queryCategory, advancedFilters.querySubcategory, advancedFilters.disposition, advancedFilters.subdisposition, advancedFilters.lead_source, advancedFilters.primary_language, advancedFilters.secondary_languages, advancedFilters.lead_sub_source, advancedFilters.tag, advancedFilters.tag2, advancedFilters.tag3, ...getAdditionalTagFilterValues(advancedFilters), advancedFilters.crm_lead_stage, advancedFilters.crm_lead_sub_stage, advancedFilters.customer_phone_number, advancedFilters.crm_location,
      advancedFilters.campaign_id,
      advancedFilters.custom_module_parameter_name, advancedFilters.custom_module_parameter_answer,
      controller.signal,
      moduleParameterFilters,
      ...getCrmLeadCreatedAtApiArgs(advancedFilters), filters.team_name
    ).then(async res => {
      if (res.status === 200) {
        const json = await res.json();
        const data = json.data || {};
        // Check if response is tag-grouped: { "TagName": { "ParamName": [...], ... }, ... }
        const isTagGrouped = !Array.isArray(data.parameters) && !Array.isArray(data.charts)
          && typeof data === 'object'
          && Object.values(data).some(v => v && typeof v === 'object' && !Array.isArray(v)
            && Object.values(v).some(inner => Array.isArray(inner)));
        if (isTagGrouped) {
          // Store as-is — same structure as pieDistribution for tag-grouped rendering
          setCustomModuleCharts(data);
        } else {
          // Legacy flat format
          let charts = [];
          if (Array.isArray(data.parameters)) {
            data.parameters.forEach((param) => {
              const title = param.display_name || param.parameter_name || 'Parameter';
              const valuesObj = param.values || param.count_map || {};
              const dataArr = Array.isArray(valuesObj) ? valuesObj : Object.entries(valuesObj).map(([name, value]) => ({ name, value }));
              if (dataArr.length > 0) charts.push({ title, data: dataArr });
              if (Array.isArray(param.subparameters)) {
                param.subparameters.forEach((sub) => {
                  const subTitle = `${title} - ${sub.display_name || sub.parameter_name || 'Subparameter'}`;
                  const subValuesObj = sub.values || sub.count_map || {};
                  const subDataArr = Array.isArray(subValuesObj) ? subValuesObj : Object.entries(subValuesObj).map(([name, value]) => ({ name, value }));
                  if (subDataArr.length > 0) charts.push({ title: subTitle, data: subDataArr });
                });
              }
            });
          } else if (Array.isArray(data.charts)) {
            charts = data.charts;
          } else if (data && typeof data === 'object') {
            charts = Object.entries(data).map(([key, arr]) => {
              const pretty = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              return { title: pretty, data: Array.isArray(arr) ? arr : [] };
            }).filter(c => Array.isArray(c.data) && c.data.length > 0);
          }
          setCustomModuleCharts(charts);
        }
      }
    })
    .catch((err) => {
      if (err?.name !== 'AbortError') {
        // eslint-disable-next-line no-console
        console.error('Custom module analytics fetch failed', err);
      }
    })
    .finally(()=> setLoadingCustomModule(false));
  }
  const getFirstResponseTimeAnalytics = () => {
    if(firstResponseTimeAbortRef.current){ firstResponseTimeAbortRef.current.abort(); }
    const controller = new AbortController();
    firstResponseTimeAbortRef.current = controller;
    setLoadingFirstResponseTime(true);
    const moduleParameterFilters = buildModuleParameterFilters(filters, advancedFilters);
    const commonArgs = [
      client_id, service_id, email, 'customer-insights', undefined,
      advancedFilters.id, advancedFilters.customerPotential, advancedFilters.interactions, advancedFilters.openConcerns,
      filters.employee, filters.lastinteraction, filters.firstinteraction,
      filters['Last Interaction Start'], filters['Last Interaction End'],
      filters['First Interaction Start'], filters['First Interaction End'],
      advancedFilters.business_insights_multiple, advancedFilters.concernCategory, advancedFilters.concernSubcategory, advancedFilters.queryCategory, advancedFilters.querySubcategory, advancedFilters.disposition, advancedFilters.subdisposition, advancedFilters.lead_source, advancedFilters.primary_language, advancedFilters.secondary_languages, advancedFilters.lead_sub_source, advancedFilters.tag, advancedFilters.tag2, advancedFilters.tag3, ...getAdditionalTagFilterValues(advancedFilters), advancedFilters.crm_lead_stage, advancedFilters.crm_lead_sub_stage, advancedFilters.customer_phone_number, advancedFilters.crm_location,
      advancedFilters.campaign_id,
      advancedFilters.custom_module_parameter_name, advancedFilters.custom_module_parameter_answer,
      controller.signal,
      moduleParameterFilters,
      ...getCrmLeadCreatedAtApiArgs(advancedFilters), filters.team_name
    ];

    Promise.allSettled([
      server.getFirstResponseTimePerformanceMetrics(...commonArgs),
      server.getFirstResponseTimeAgentWise(...commonArgs),
      server.getFirstResponseTimeDailyTrend(...commonArgs),
    ]).then(async ([metricsRes, agentRes, trendRes]) => {
      if (firstResponseTimeAbortRef.current !== controller) return;
      const metricsResponse = metricsRes.status === 'fulfilled' ? metricsRes.value : null;
      const agentResponse = agentRes.status === 'fulfilled' ? agentRes.value : null;
      const trendResponse = trendRes.status === 'fulfilled' ? trendRes.value : null;
      let nextOverallMetrics = null;
      let nextAgentRows = [];
      let nextAgentMetrics = [];
      let nextTrendRows = [];
      let nextTrendMetrics = [];

      if (metricsResponse && metricsResponse.status === 200) {
        const json = await metricsResponse.json();
        nextOverallMetrics = json.data || json;
      } else if (metricsRes.status === 'rejected' && metricsRes.reason?.name !== 'AbortError') {
        // eslint-disable-next-line no-console
        console.error('First response time metrics fetch failed', metricsRes.reason);
      }

      if (agentResponse && agentResponse.status === 200) {
        const json = await agentResponse.json();
        const payload = json.data || json;
        nextAgentRows = normalizeFirstResponseRows(payload, 'employee_id');
        nextAgentMetrics = buildFirstResponseMetrics(nextOverallMetrics || payload.metrics || payload.summary, nextAgentRows);
      } else if (agentRes.status === 'rejected' && agentRes.reason?.name !== 'AbortError') {
        // eslint-disable-next-line no-console
        console.error('First response time agent-wise fetch failed', agentRes.reason);
      }

      if (trendResponse && trendResponse.status === 200) {
        const json = await trendResponse.json();
        const payload = json.data || json;
        nextTrendRows = normalizeFirstResponseRows(payload, 'date');
        nextTrendMetrics = buildFirstResponseMetrics(nextOverallMetrics || payload.metrics || payload.summary, nextTrendRows);
      } else if (trendRes.status === 'rejected' && trendRes.reason?.name !== 'AbortError') {
        // eslint-disable-next-line no-console
        console.error('First response time daily trend fetch failed', trendRes.reason);
      }

      setFirstResponseAgentWiseData(nextAgentRows);
      setFirstResponseAgentMetrics(nextAgentMetrics);
      setFirstResponseDailyTrendData(nextTrendRows);
      setFirstResponseTrendMetrics(nextTrendMetrics);
    }).catch((err) => {
      if (firstResponseTimeAbortRef.current === controller && err?.name !== 'AbortError') {
        // eslint-disable-next-line no-console
        console.error('First response time analytics fetch failed', err);
        setFirstResponseAgentWiseData([]);
        setFirstResponseAgentMetrics([]);
        setFirstResponseDailyTrendData([]);
        setFirstResponseTrendMetrics([]);
      }
    }).finally(() => {
      if (firstResponseTimeAbortRef.current === controller) {
        setLoadingFirstResponseTime(false);
      }
    });
  }
  const fetchFilterDetailsOnce = async () => {
    setFilterDetailsLoaded(false);
    try{
      const controller = new AbortController();
      const res = await server.getCustomerFilters(client_id, service_id, email, 'customer-insights', undefined, controller.signal);
      if(res.status === 200){
        const data = await res.json();
        setMultiSelectFields(Array.isArray(data.multiselects_list) ? data.multiselects_list : []);
        setDummyAdvancedFilterData(prev=>({
          ...prev,
          employees: data.employee_id,
          teams: data.team_names || data.team_name || data.teams || [],
          customer_outcome: data.customer_outcome,
          refusal_category: data.refusal_category,
          customer_potential: data.customer_potential,
          business_insight_parameters: data.business_insights,
          concern_categories: data.concerns_category,
          concern_subcategories: data.concerns_subcategory,
          query_categories: data.query_category,
          query_subcategories: data.query_subcategory,
          disposition: data.disposition,
          subdisposition: data.subdisposition,
          lead_sources: data.lead_sources,
          primary_languages: data.primary_languages || data.primary_language || [],
          secondary_languages: data.secondary_languages || [],
          lead_sub_sources: data.lead_sub_sources,
          tags: data.tags,
          tags2: data.tags2 || data.tag2 || data.tags,
          tags3: data.tags3 || data.tag3 || data.tags,
          tags4: data.tags4 || data.tag4 || data.tags,
          tags5: data.tags5 || data.tag5 || data.tags,
          tags6: data.tags6 || data.tag6 || data.tags,
          tags7: data.tags7 || data.tag7 || data.tags,
          tags8: data.tags8 || data.tag8 || data.tags,
          tags9: data.tags9 || data.tag9 || data.tags,
          tags10: data.tags10 || data.tag10 || data.tags,
          tags11: data.tags11 || data.tag11 || data.tags,
          tags12: data.tags12 || data.tag12 || data.tags,
          tags13: data.tags13 || data.tag13 || data.tags,
          tags14: data.tags14 || data.tag14 || data.tags,
          tags15: data.tags15 || data.tag15 || data.tags,
          tags16: data.tags16 || data.tag16 || data.tags,
          tags17: data.tags17 || data.tag17 || data.tags,
          tags18: data.tags18 || data.tag18 || data.tags,
          tags19: data.tags19 || data.tag19 || data.tags,
          tags20: data.tags20 || data.tag20 || data.tags,
          tags21: data.tags21 || data.tag21 || data.tags,
          tags22: data.tags22 || data.tag22 || data.tags,
          tags23: data.tags23 || data.tag23 || data.tags,
          tags24: data.tags24 || data.tag24 || data.tags,
          tags25: data.tags25 || data.tag25 || data.tags,
          tags26: data.tags26 || data.tag26 || data.tags,
          tags27: data.tags27 || data.tag27 || data.tags,
          tags28: data.tags28 || data.tag28 || data.tags,
          tags29: data.tags29 || data.tag29 || data.tags,
          tags30: data.tags30 || data.tag30 || data.tags,
          agent_feedback_parameters: data.agent_feedback_parameters,
          lead_stages: data.lead_stages,
          lead_sub_stages: data.lead_sub_stages,
          custom_module: data.custom_module,
          campaign_ids: data.campaign_ids
        }));
      }
    }catch(err){
      if(err.name === 'AbortError'){
        return; // silently ignore aborted request
      }
    }finally{
      setFilterDetailsLoaded(true);
    }
  }
  const handleDownload = (data, title, options = {}) => {
    if (options.workbook) {
      downloadWorkbook(data, `${title}_data.xlsx`);
      return;
    }
    downloadRowsAsWorkbook(data, title);
  }
  const formatTime = (seconds) => {
    const totalSeconds = Number(seconds);
    if (!Number.isFinite(totalSeconds)) return 'N/A';
    const normalizedSeconds = Math.floor(totalSeconds);
    const hours = Math.floor(normalizedSeconds / 3600);
    const minutes = Math.floor((normalizedSeconds % 3600) / 60);
    const remainingSeconds = normalizedSeconds % 60;
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  };
  const formatDate = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return String(dateValue);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };
  const getMetricNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const getRowMetricValue = (row, key) => getMetricNumber(row?.[key] ?? row?.[toSnakeCase(key)]);
  const getAverageMetricValue = (rows, key) => {
    if (!Array.isArray(rows) || rows.length === 0) return 0;
    const values = rows.map((row) => getRowMetricValue(row, key)).filter((value) => Number.isFinite(value));
    if (values.length === 0) return 0;
    return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
  };
  const getMetricChangeValue = (metrics, key) => {
    const metricSource = metrics?.data || metrics;
    if (Array.isArray(metrics)) {
      const metric = metrics.find((item) => item?.id === key || item?.name === key);
      return metric?.change ?? metric?.percentage ?? metric?.percent_change ?? 0;
    }
    return metricSource?.percentage_change?.[key]
      ?? metricSource?.[`${key}_change`]
      ?? metricSource?.[`${key}_percentage`]
      ?? metricSource?.[key]?.change
      ?? 0;
  };
  const getMetricValueFromResponse = (metrics, rows, key) => {
    const metricSource = metrics?.data || metrics;
    if (Array.isArray(metrics)) {
      const metric = metrics.find((item) => item?.id === key || item?.name === key);
      if (metric && (metric.value !== undefined || metric.avg !== undefined)) return getMetricNumber(metric.value ?? metric.avg);
    }
    if (metricSource && typeof metricSource === 'object') {
      const metricValue = metricSource?.last_current_period?.[key]
        ?? metricSource?.[key]?.value
        ?? metricSource?.[key]?.avg
        ?? metricSource?.[key];
      if (metricValue !== undefined && typeof metricValue !== 'object') return getMetricNumber(metricValue);
    }
    return getAverageMetricValue(rows, key);
  };
  const buildFirstResponseMetrics = (sourceMetrics, rows) => ([
    {
      id: 'frt',
      title: 'First Response Time',
      value: getMetricValueFromResponse(sourceMetrics, rows, 'frt'),
      change: getMetricChangeValue(sourceMetrics, 'frt'),
      color: '#4F46E5',
      allowVisit: false,
      formatter: formatTime,
    },
    {
      id: 'frt_business_hours',
      title: 'First Response Time (Business Hours)',
      value: getMetricValueFromResponse(sourceMetrics, rows, 'frt_business_hours'),
      change: getMetricChangeValue(sourceMetrics, 'frt_business_hours'),
      color: '#4F46E5',
      allowVisit: false,
      formatter: formatTime,
    },
  ]);
  const normalizeFirstResponseRows = (rawData, fallbackNameKey = 'name') => {
    const source = Array.isArray(rawData)
      ? rawData
      : (rawData?.data || rawData?.rows || rawData?.agent_wise || rawData?.daily_trend || []);
    if (!Array.isArray(source)) return [];
    return source.map((item) => ({
      ...item,
      name: item?.name || item?.employee_id || item?.agent || item?.[fallbackNameKey] || 'Unknown',
      date: item?.date || item?.day || item?.name || item?.[fallbackNameKey],
      frt: getMetricNumber(item?.frt ?? item?.first_response_time ?? item?.first_resolution_time),
      frt_business_hours: getMetricNumber(item?.frt_business_hours ?? item?.first_response_time_business_hours ?? item?.first_resolution_time_business_hours),
    }));
  };
  // CSV helpers for Sankey
  const sanitizeCsvValue = (value) => {
    const s = value == null ? '' : String(value);
    return `"${s.replace(/"/g, '""')}"`;
  };
  const extractSankeyRowsWithCount = (sankey) => {
    if (!sankey || !Array.isArray(sankey.nodes) || !Array.isArray(sankey.links)) return [];
    const nodes = sankey.nodes;
    const links = sankey.links;
    // find root index (prefer node named "Total")
    let rootIndex = nodes.findIndex(n => (n && n.name || '').toLowerCase() === 'total');
    if (rootIndex === -1) {
      const incoming = new Set(links.map(l => l.target));
      const noIncoming = nodes.findIndex((_, i) => !incoming.has(i));
      rootIndex = noIncoming !== -1 ? noIncoming : 0;
    }
    // adjacency for children
    const adjacency = {};
    links.forEach(l => {
      if (!adjacency[l.source]) adjacency[l.source] = [];
      adjacency[l.source].push(l.target);
    });
    // compute depths from root using BFS
    const depth = new Array(nodes.length).fill(Infinity);
    depth[rootIndex] = 1;
    const q = [rootIndex];
    while (q.length) {
      const cur = q.shift();
      const children = adjacency[cur] || [];
      children.forEach((child) => {
        if (depth[child] > depth[cur] + 1) {
          depth[child] = depth[cur] + 1;
          q.push(child);
        }
      });
    }
    // build rows for links from level 2 -> level 3; count from link.value
    const rootName = (nodes[rootIndex] && nodes[rootIndex].name) ? nodes[rootIndex].name : 'Total';
    const rows = [];
    links.forEach(l => {
      if (depth[l.source] === 2 && depth[l.target] === 3) {
        const lvl2 = nodes[l.source] && nodes[l.source].name ? nodes[l.source].name : '';
        const lvl3 = nodes[l.target] && nodes[l.target].name ? nodes[l.target].name : '';
        rows.push([rootName, lvl2, lvl3, l.value]);
      }
    });
    return rows;
  };
  const handleSankeyDownload = () => {
    if (!dispositionData || !dispositionData.links || dispositionData.links.length === 0) return;
    const rows = extractSankeyRowsWithCount(dispositionData);
    const header = ['Total', 'Disposition', 'Sub Disposition', 'Count'];
    const csvRows = [header, ...rows];
    const csvString = csvRows.map(row => row.map(sanitizeCsvValue).join(",")).join("\r\n");
    const blob = new Blob(["\ufeff", csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "dispositions_sankey.csv";
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(url);
  }
  // Function to get disposition keys (for the disposition dropdown)
  const getDispositionKeys = () => {
    if (!dummyAdvancedFilterData.subdisposition || typeof dummyAdvancedFilterData.subdisposition !== 'object') {
      return dummyAdvancedFilterData.disposition || [];
    }
    const keys = Object.keys(dummyAdvancedFilterData.subdisposition);
    return keys;
  };

  const getSubdispositionsForDisposition = (selectedDisposition) => {
    if (!dummyAdvancedFilterData.subdisposition) {
      return [];
    }
    
    // If no disposition selected, return all subdispositions
    if (!selectedDisposition) {
      if (typeof dummyAdvancedFilterData.subdisposition === 'object' && !Array.isArray(dummyAdvancedFilterData.subdisposition)) {
        // Flatten all subdispositions from all dispositions
        return Object.values(dummyAdvancedFilterData.subdisposition).flat();
      }
      return dummyAdvancedFilterData.subdisposition || [];
    }
    
    // If disposition selected, return only relevant subdispositions
    if (typeof dummyAdvancedFilterData.subdisposition === 'object' && !Array.isArray(dummyAdvancedFilterData.subdisposition)) {
      const subdispositions = dummyAdvancedFilterData.subdisposition[selectedDisposition] || [];
      return subdispositions;
    }
    
    return dummyAdvancedFilterData.subdisposition || [];
  };

  // Enhanced handler for disposition changes
  const handleDispositionChange = (e) => {
    const newDisposition = e.target.value;
    
    setAdvancedFilters(prev => ({
      ...prev,
      disposition: newDisposition
    }));
    
    if (newDisposition) {
      const validSubdispositions = getSubdispositionsForDisposition(newDisposition);
      const currentSubdisposition = advancedFilters.subdisposition;
      
      // If current subdisposition is not valid for new disposition, clear it
      if (currentSubdisposition && !validSubdispositions.includes(currentSubdisposition)) {
        setAdvancedFilters(prev => ({
          ...prev,
          subdisposition: ''
        }));
      }
    } else {
      setAdvancedFilters(prev => ({
        ...prev,
        subdisposition: ''
      }));
    }
  };

  // Function to get concern category keys (for the concern category dropdown)
  const getConcernCategoryKeys = () => {
    if (!dummyAdvancedFilterData.concern_subcategories || typeof dummyAdvancedFilterData.concern_subcategories !== 'object') {
      return dummyAdvancedFilterData.concern_categories || [];
    }
    const keys = Object.keys(dummyAdvancedFilterData.concern_subcategories);
    return keys;
  };

  const getConcernSubcategoriesForCategory = (selectedConcernCategory) => {
    if (!dummyAdvancedFilterData.concern_subcategories) {
      return [];
    }
    
    // If no concern category selected, return all subcategories
    if (!selectedConcernCategory) {
      if (typeof dummyAdvancedFilterData.concern_subcategories === 'object' && !Array.isArray(dummyAdvancedFilterData.concern_subcategories)) {
        // Flatten all subcategories from all categories
        return Object.values(dummyAdvancedFilterData.concern_subcategories).flat();
      }
      return dummyAdvancedFilterData.concern_subcategories || [];
    }
    
    // If concern category selected, return only relevant subcategories
    if (typeof dummyAdvancedFilterData.concern_subcategories === 'object' && !Array.isArray(dummyAdvancedFilterData.concern_subcategories)) {
      const subcategories = dummyAdvancedFilterData.concern_subcategories[selectedConcernCategory] || [];
      return subcategories;
    }
    
    return dummyAdvancedFilterData.concern_subcategories || [];
  };

  // Enhanced handler for concern category changes
  const handleConcernCategoryChange = (e) => {
    const newConcernCategory = e.target.value;
    
    setAdvancedFilters(prev => ({
      ...prev,
      concernCategory: newConcernCategory
    }));
    
    if (newConcernCategory) {
      const validSubcategories = getConcernSubcategoriesForCategory(newConcernCategory);
      const currentSubcategory = advancedFilters.concernSubcategory;
      
      // If current subcategory is not valid for new category, clear it
      if (currentSubcategory && !validSubcategories.includes(currentSubcategory)) {
        setAdvancedFilters(prev => ({
          ...prev,
          concernSubcategory: ''
        }));
      }
    } else {
      setAdvancedFilters(prev => ({
        ...prev,
        concernSubcategory: ''
      }));
    }
  };

  // Function to get query category keys (for the query category dropdown)
  const getQueryCategoryKeys = () => {
    if (!dummyAdvancedFilterData.query_subcategories || typeof dummyAdvancedFilterData.query_subcategories !== 'object') {
      return dummyAdvancedFilterData.query_categories || [];
    }
    const keys = Object.keys(dummyAdvancedFilterData.query_subcategories);
    return keys;
  };

  const getQuerySubcategoriesForCategory = (selectedQueryCategory) => {
    if (!dummyAdvancedFilterData.query_subcategories) {
      return [];
    }
    
    // If no query category selected, return all subcategories
    if (!selectedQueryCategory) {
      if (typeof dummyAdvancedFilterData.query_subcategories === 'object' && !Array.isArray(dummyAdvancedFilterData.query_subcategories)) {
        // Flatten all subcategories from all categories
        return Object.values(dummyAdvancedFilterData.query_subcategories).flat();
      }
      return dummyAdvancedFilterData.query_subcategories || [];
    }
    
    // If query category selected, return only relevant subcategories
    if (typeof dummyAdvancedFilterData.query_subcategories === 'object' && !Array.isArray(dummyAdvancedFilterData.query_subcategories)) {
      const subcategories = dummyAdvancedFilterData.query_subcategories[selectedQueryCategory] || [];
      return subcategories;
    }
    
    return dummyAdvancedFilterData.query_subcategories || [];
  };
  // Custom Module helpers
  const getCustomModuleKeys = () => {
    if (!Array.isArray(dummyAdvancedFilterData.custom_module)) return [];
    return dummyAdvancedFilterData.custom_module.map(([key]) => key);
  };
  const getCustomModuleValues = (selectedParameter) => {
    if (!Array.isArray(dummyAdvancedFilterData.custom_module) || !selectedParameter) return [];
    const match = dummyAdvancedFilterData.custom_module.find(([key]) => key === selectedParameter);
    return match ? (Array.isArray(match[1]) ? match[1] : []) : [];
  };
  const handleCustomModuleParameterChange = (e) => {
    const param = e.target.value;
    setAdvancedFilters(prev => ({
      ...prev,
      custom_module_parameter_name: param,
      custom_module_parameter_answer: getCustomModuleValues(param).includes(prev.custom_module_parameter_answer) ? prev.custom_module_parameter_answer : ''
    }));
  };

  // Enhanced handler for query category changes
  const handleQueryCategoryChange = (e) => {
    const newQueryCategory = e.target.value;
    
    setAdvancedFilters(prev => ({
      ...prev,
      queryCategory: newQueryCategory
    }));
    
    if (newQueryCategory) {
      const validSubcategories = getQuerySubcategoriesForCategory(newQueryCategory);
      const currentSubcategory = advancedFilters.querySubcategory;
      
      // If current subcategory is not valid for new category, clear it
      if (currentSubcategory && !validSubcategories.includes(currentSubcategory)) {
        setAdvancedFilters(prev => ({
          ...prev,
          querySubcategory: ''
        }));
      }
    } else {
      setAdvancedFilters(prev => ({
        ...prev,
        querySubcategory: ''
      }));
    }
  };
  useEffect(()=>{
    if(client_id && service_id && email){
      fetchFilterDetailsOnce();
    }
  }, [client_id, service_id, email]);
  
  function getCookie(name) {
        function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
        var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
        return match ? match[1] : null;
    }
    const toggleAdvancedFilterPanel = () => {
      setShowAdvancedFilterPanel(!showAdvancedFilterPanel);
  };
  const handleAdvancedInputChange = (e, key) => {
    const value = e.target.value;
    
    // Special handling for business insight parameter selection
    if (key === 'businessInsightParameter') {
      setAdvancedFilters((prev) => ({
        ...prev,
        [key]: value,
        businessInsightValue: '', // Reset value when parameter changes
      }));
    } else {
      setAdvancedFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
    }
  };
  const clearModuleFilterByBaseKey = (baseKey) => {
    setFilters((prev) => ({
      ...prev,
      [baseKey]: "",
      [`${baseKey}_lower`]: "",
      [`${baseKey}_upper`]: "",
    }));

    if (baseKey.startsWith("custom_module_")) {
      const moduleField = baseKey.replace(/^custom_module_/, "");
      setAdvancedFilters((prev) => {
        const nextCustomModuleRows = (prev.custom_module_multiple || []).filter(
          (item) => toSnakeCase(item?.custom_module_parameter_name) !== moduleField
        );
        const isPrimaryMatch =
          toSnakeCase(prev.custom_module_parameter_name) === moduleField;
        return {
          ...prev,
          custom_module_multiple: nextCustomModuleRows,
          ...(isPrimaryMatch
            ? {
                custom_module_parameter_name: "",
                custom_module_parameter_answer: "",
              }
            : {}),
        };
      });
      return;
    }

    if (baseKey.startsWith("business_insight_")) {
      const moduleField = baseKey.replace(/^business_insight_/, "");
      setAdvancedFilters((prev) => ({
        ...prev,
        business_insights_multiple: (prev.business_insights_multiple || []).filter(
          (item) => toSnakeCase(item?.business_insights_parameter) !== moduleField
        ),
      }));
    }
  };
    const resolveCustomModuleParameter = (candidate) => {
      const keys = getCustomModuleKeys();
      const normalizedCandidate = normalizeKey(candidate);
      if (!normalizedCandidate) return '';
      const direct = keys.find((k) => normalizeKey(k) === normalizedCandidate);
      return direct || '';
    };

    const handleSegmentClick = (data) => {
      data['type'] = 'business_insights'
      data['filter_applied'] = {...filters, ...advancedFilters, ...moduleParameterQueryFilters};
      if (selectedAnalyticsTab === 'custom_module') {
        const selectedParameter = resolveCustomModuleParameter(data?.__parameterKey || data?.parameter || data?.title);
        const selectedValue = data?.name ?? '';
        if (selectedParameter && selectedValue !== '') {
          data['type'] = 'custom_module';
          data['filter_applied']['custom_module_parameter_name'] = selectedParameter;
          data['filter_applied']['custom_module_parameter_answer'] = selectedValue;
          const existingRows = Array.isArray(data['filter_applied']['custom_module_multiple'])
            ? data['filter_applied']['custom_module_multiple']
            : [];
          const nextRows = [
            ...existingRows.filter((row) => normalizeKey(row?.custom_module_parameter_name) !== normalizeKey(selectedParameter)),
            {
              custom_module_parameter_name: selectedParameter,
              custom_module_parameter_answer: selectedValue,
            },
          ];
          data['filter_applied']['custom_module_multiple'] = nextRows;
          data['filter_applied'][`custom_module_${toSnakeCase(selectedParameter)}`] = selectedValue;
        }
      }
      if (filters['Last Interaction Start'] && (filters.lastinteraction_range_type === "after" || filters['Last Interaction End'])) {
        data['filter_applied']['Last Interaction Start'] = filters['Last Interaction Start']
        if (filters['Last Interaction End']) data['filter_applied']['Last Interaction End'] = filters['Last Interaction End']
        if (filters.lastinteraction_range_type) {
          data['filter_applied'].lastinteraction_range_type = filters.lastinteraction_range_type
        }
      }
      if (filters['First Interaction Start'] && (filters.firstinteraction_range_type === "after" || filters['First Interaction End'])) {
        data['filter_applied']['First Interaction Start'] = filters['First Interaction Start']
        if (filters['First Interaction End']) data['filter_applied']['First Interaction End'] = filters['First Interaction End']
        if (filters.firstinteraction_range_type) {
          data['filter_applied'].firstinteraction_range_type = filters.firstinteraction_range_type
        }
      }
        setSelectedSegment(data);
        setIsModalOpen(true);
    };
    const handleLeadQualityClick = (data) => {
        let obj = {'name': data.name}
      obj['type'] = 'business_insights_lead_quality'
      obj['id'] = 'Lead Quality'
      obj['filter_applied'] = {...filters, ...advancedFilters, ...moduleParameterQueryFilters};
      if (filters['Last Interaction Start'] && (filters.lastinteraction_range_type === "after" || filters['Last Interaction End'])) {
        obj['filter_applied']['Last Interaction Start'] = filters['Last Interaction Start']
        if (filters['Last Interaction End']) obj['filter_applied']['Last Interaction End'] = filters['Last Interaction End']
        if (filters.lastinteraction_range_type) {
          obj['filter_applied'].lastinteraction_range_type = filters.lastinteraction_range_type
        }
      }
      if (filters['First Interaction Start'] && (filters.firstinteraction_range_type === "after" || filters['First Interaction End'])) {
        obj['filter_applied']['First Interaction Start'] = filters['First Interaction Start']
        if (filters['First Interaction End']) obj['filter_applied']['First Interaction End'] = filters['First Interaction End']
        if (filters.firstinteraction_range_type) {
          obj['filter_applied'].firstinteraction_range_type = filters.firstinteraction_range_type
        }
      }
      setSelectedSegment(obj)
      setIsModalOpen(true);
    }
    const handleSankeyClick = (data) => {
      const clickedName = typeof data === 'string' ? data : (data && data.name ? data.name : data);
      let obj = { 'name': clickedName };
      obj['type'] = 'business_insights_sankey';
      if (!advancedFilters.disposition || advancedFilters.disposition === '') {
        obj['id'] = 'Disposition / Sub-disposition';
      }

      // Determine whether the clicked node is a disposition or a subdisposition
      const forced = {};
      const allDispositions = getDispositionKeys(); // disposition keys

      if (Array.isArray(allDispositions) && allDispositions.includes(clickedName)) {
        // Clicked on a disposition node
        forced.disposition = clickedName;
      } else {
        // Try to resolve clickedName as a subdisposition and find its parent disposition
        const subMap = dummyAdvancedFilterData.subdisposition;
        if (subMap && typeof subMap === 'object' && !Array.isArray(subMap)) {
          for (const [parent, subs] of Object.entries(subMap)) {
            if (Array.isArray(subs) && subs.includes(clickedName)) {
              forced.disposition = parent;
              forced.subdisposition = clickedName;
              break;
            }
          }
          if (!forced.subdisposition) {
            // Fallback if mapping style is unexpected: flatten and check
            const allSubs = Object.values(subMap).flat();
            if (allSubs.includes(clickedName)) {
              forced.subdisposition = clickedName;
            }
          }
        } else if (Array.isArray(subMap)) {
          // Legacy array-only form
          if (subMap.includes(clickedName)) {
            forced.subdisposition = clickedName;
          }
        }
      }

      obj['filter_applied'] = { ...filters, ...advancedFilters, ...moduleParameterQueryFilters, ...forced };
      if (filters['Last Interaction Start'] && (filters.lastinteraction_range_type === "after" || filters['Last Interaction End'])) {
        obj['filter_applied']['Last Interaction Start'] = filters['Last Interaction Start'];
        if (filters['Last Interaction End']) obj['filter_applied']['Last Interaction End'] = filters['Last Interaction End'];
        if (filters.lastinteraction_range_type) {
          obj['filter_applied'].lastinteraction_range_type = filters.lastinteraction_range_type;
        }
      }
      if (filters['First Interaction Start'] && (filters.firstinteraction_range_type === "after" || filters['First Interaction End'])) {
        obj['filter_applied']['First Interaction Start'] = filters['First Interaction Start'];
        if (filters['First Interaction End']) obj['filter_applied']['First Interaction End'] = filters['First Interaction End'];
        if (filters.firstinteraction_range_type) {
          obj['filter_applied'].firstinteraction_range_type = filters.firstinteraction_range_type;
        }
      }
      setSelectedSegment(obj);
      setIsModalOpen(true);
    }
    
    useEffect(() => {
                const user = getCookie('dashboard_login_user_creds') ? JSON.parse(getCookie('dashboard_login_user_creds')) : null;
                if (user) {
                  setEmail(user.email);
                }
                if (serviceID) {
                  setService_id(serviceID);
                }
                if(clientID){
                  setClient_id(clientID)
                }
                if(role === 'employee'){
                  handleFilterChange({'target': {'value': user.name}}, 'employee')
                }
              }, [serviceID, clientID, role]);
            
    useEffect(() => {
                if (client_id && service_id && email) {
      getMetrics();
      getFirstResponseTimeAnalytics();
      if (selectedAnalyticsTab === 'custom_module') {
        getCustomModuleAnalytics();
      } else if (selectedAnalyticsTab === 'business') {
        getBusinessInsightPie();
      }
                }
              }, [client_id, service_id, email]);

              useEffect(() => {
                if (!client_id || !service_id) return;
            
                // Skip debounce logic on initial mount to avoid duplicate first fetch
                if (firstFiltersLoadRef.current) {
                  firstFiltersLoadRef.current = false;
                  return;
                }
            
                // Clear previous debounce timer
                if (filtersDebounceRef.current) {
                  clearTimeout(filtersDebounceRef.current);
                }
            
                filtersDebounceRef.current = setTimeout(() => {
                  // Always refresh Disposition & Lead Quality
                  getMetrics();
                  getFirstResponseTimeAnalytics();
                  // Fetch tab-specific analytics
                  if (selectedAnalyticsTab === 'custom_module') {
                    getCustomModuleAnalytics();
                  } else if (selectedAnalyticsTab === 'business') {
                    getBusinessInsightPie();
                  }
                }, 600); // idle delay
            
                return () => {
                  if (filtersDebounceRef.current) {
                    clearTimeout(filtersDebounceRef.current);
                  }
                };
              }, [filters, advancedFilters, client_id, service_id]);

  const initialAnalyticsTabRef = useRef(selectedAnalyticsTab);
  const tabSwitchInitializedRef = useRef(false);
  useEffect(() => {
    if (!client_id || !service_id || !email) return;
    if (!tabSwitchInitializedRef.current) {
      tabSwitchInitializedRef.current = true;
      if (selectedAnalyticsTab === initialAnalyticsTabRef.current) return;
    }
    if (selectedAnalyticsTab === 'custom_module') {
      getCustomModuleAnalytics();
    } else if (selectedAnalyticsTab === 'business') {
      getBusinessInsightPie();
    } else if (selectedAnalyticsTab === 'first_response_time') {
      getFirstResponseTimeAnalytics();
    }
  }, [selectedAnalyticsTab, client_id, service_id, email]);

  const hasConfiguredValues = (value) => {
    if (Array.isArray(value)) return value.length > 0;
    if (value && typeof value === 'object') {
      return Object.values(value).some((item) => hasConfiguredValues(item));
    }
    return value !== undefined && value !== null && value !== '';
  };

  const showCustomModuleTab = hasConfiguredValues(dummyAdvancedFilterData.custom_module)
    || hasConfiguredValues(customModuleCharts);
  const showBusinessInsightsTab = hasConfiguredValues(dummyAdvancedFilterData.business_insight_parameters)
    || hasConfiguredValues(pieDistribution);
  const shouldShowPendingTab = (tabId, loading) =>
    Boolean(loading && (filterDetailsLoaded || selectedAnalyticsTab === tabId));
  const showFirstResponseTimeTab = Boolean(firstResponseAgentWiseData?.length)
    || Boolean(firstResponseDailyTrendData?.length)
    || shouldShowPendingTab('first_response_time', loadingFirstResponseTime);
  const showConversationDispositionTab = Boolean(dispositionData?.links?.length)
    || hasConfiguredValues(dummyAdvancedFilterData.disposition)
    || hasConfiguredValues(dummyAdvancedFilterData.subdisposition)
    || shouldShowPendingTab('conversation_disposition', loadingDisposition);
  const showLeadQualityTab = Boolean(leadQualityData?.length)
    || hasConfiguredValues(dummyAdvancedFilterData.customer_potential)
    || shouldShowPendingTab('lead_quality', loadingLeadQuality);
  const availableAnalyticsTabs = [
    { id: 'custom_module', show: showCustomModuleTab },
    { id: 'business', show: showBusinessInsightsTab },
    { id: 'first_response_time', show: showFirstResponseTimeTab },
    { id: 'conversation_disposition', show: showConversationDispositionTab },
    { id: 'lead_quality', show: showLeadQualityTab },
  ];
  const firstAvailableAnalyticsTab = availableAnalyticsTabs.find((tab) => tab.show)?.id || null;
  const selectedAnalyticsTabAvailable = availableAnalyticsTabs.some(
    (tab) => tab.id === selectedAnalyticsTab && tab.show
  );

  useEffect(() => {
    if (firstAvailableAnalyticsTab && !selectedAnalyticsTabAvailable) {
      setSelectedAnalyticsTab(firstAvailableAnalyticsTab);
    }
  }, [firstAvailableAnalyticsTab, selectedAnalyticsTabAvailable]);

    useEffect(() => {
      const handleEsc = (event) => {
        if (event.key === "Escape") {
          setShowAdvancedFilterPanel(false);
          setIsModalOpen(false);
        }
      }
      if (showAdvancedFilterPanel || isModalOpen) {
        window.addEventListener('keydown', handleEsc);
      }
      return () => {
        window.removeEventListener('keydown', handleEsc);
      }
    }, [showAdvancedFilterPanel, isModalOpen])

    const renderTabLoadingOrEmpty = (loading, emptyText) => (
      <div className="p-4">
        <div className="flex justify-center items-center text-sm text-gray-500" style={{ minHeight: "25rem", flexDirection: "column" }}>
          {loading ? (
            <>
              <div className="loader"></div>
              <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
            </>
          ) : (
            emptyText
          )}
        </div>
      </div>
    );
    
    const PieChartsGrid = () => {
                if (!pieDistribution || !hasConfiguredValues(pieDistribution)) {
                  return renderTabLoadingOrEmpty(loadingPieDist, "No business insights data available.");
                }
                return (
                  <div className="p-4">
                    {Object.entries(pieDistribution).map(([category, charts]) => (
                      <div key={category} className="mb-6">
                        <div className="text-xl font-semibold mb-3">{category}</div>
                        <div className="grid grid-cols-3 gap-4">
                          {Object.entries(charts).map(([title, data], index) => (
                            <div key={index} className="col-span-1 rounded-md" style={{background: "white", minHeight: "25rem"}}>
                                {
                                    loadingPieDist && (
                                        <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                                        <div className="loader"></div>
                                        <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                                        </div>
                                    )
                                    }
                              {!loadingPieDist && data.length < 10 && <PieChart2 
                                onSegmentClick={handleSegmentClick} 
                                pieData={data} 
                                width="w-full" 
                                colors={colors} 
                                title={title}  
                                handleDownload={handleDownload}
                              />}
                              {
                                !loadingPieDist && data.length >= 10 && (
                                  <div className="overflow-x-auto px-3" style={{height: "28rem", overflowY: "auto"}}>
                                    <div className="flex justify-between items-center sticky top-0 left-0 w-full bg-white">
                                      <div className="text-xl font-semibold ml-3 mt-3 mb-3">{title}</div>
                                      <div><Download className="text-gray-500 hover:text-gray-700 cursor-pointer mr-3 mt-3 mb-3" size={20} onClick={()=>handleDownload(data, title)} /></div>
                                    </div>
                                    <table className="min-w-full">
                                <thead>
                                <tr className="bg-gray-50 text-left">
                                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                {data.map((item, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm text-gray-900">{item['name']}</td>
                                    <td onClick={()=>handleSegmentClick(item)} className="px-4 py-3 text-sm text-gray-900 cursor-pointer text-indigo-500 hover:text-indigo-700 underline">{item['value']}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                                  </div>
                                )
                              }
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              };
    const renderFirstResponseTimeCharts = () => {
      const metrics = firstResponseAgentMetrics.length > 0 ? firstResponseAgentMetrics : buildFirstResponseMetrics(null, firstResponseAgentWiseData);
      const trendMetrics = firstResponseTrendMetrics.length > 0 ? firstResponseTrendMetrics : buildFirstResponseMetrics(null, firstResponseDailyTrendData);
      return (
        <div className="p-4">
          {loadingFirstResponseTime && (
            <div className="flex justify-center items-center" style={{ height: "40vh", flexDirection: "column" }}>
              <div className="loader"></div>
              <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
            </div>
          )}
          {!loadingFirstResponseTime && (
            <>
              <div className="py-3 flex items-center">
                <div style={{height: "65vh", width: "100%", background: "white"}}>
                  <HorizontalBarMetrics
                    data={firstResponseAgentWiseData}
                    metrics={metrics}
                    activeMetric={activeFirstResponseAgentMetric}
                    setActiveMetric={setActiveFirstResponseAgentMetric}
                    formatTime={formatTime}
                    title="First Response Time - Agent Wise"
                    handleDownload={handleDownload}
                  />
                </div>
              </div>
              <div className="py-3 flex items-center">
                <div style={{height: "52vh", width: "100%", background: "white"}}>
                  <TrendsLineWithMetrics
                    data={firstResponseDailyTrendData}
                    metrics={trendMetrics}
                    activeMetric={activeFirstResponseTrendMetric}
                    setActiveMetric={setActiveFirstResponseTrendMetric}
                    formatDate={formatDate}
                    formatTime={formatTime}
                    title="First Response Time - Daily Trend"
                    infoText="First response time versus CRM lead created date chart."
                    defaultSmoothing="1"
                    width="w-full"
                    handleDownload={handleDownload}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      );
    };
    const renderConversationDispositionCharts = () => (
      <div className="p-4">
        <div className="sankey-charts" style={{width: "100%", background: "white", minHeight: "25rem"}}>
          {!loadingDisposition && dispositionData && dispositionData.links && dispositionData.links.length > 0 && (
            <div className="px-4 pt-3 flex items-center justify-between pb-4">
              <div className="text-xl font-semibold">Dispositions & Sub-Dispositions</div>
              <div><Download className="text-gray-500 hover:text-gray-700 cursor-pointer" size={20} onClick={handleSankeyDownload} /></div>
            </div>
          )}
          {loadingDisposition && (
            <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
              <div className="loader"></div>
              <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
            </div>
          )}
          {!loadingDisposition && dispositionData && dispositionData.links && dispositionData.links.length > 0 && (
            <DynamicSankeyChart data={dispositionData} onClickNode={handleSankeyClick}/>
          )}
          {!loadingDisposition && (!dispositionData || !dispositionData.links || dispositionData.links.length === 0) && (
            <div className="flex justify-center items-center text-sm text-gray-500" style={{ minHeight: "25rem" }}>
              No conversation disposition data available.
            </div>
          )}
        </div>
      </div>
    );
    const renderLeadQualityCharts = () => (
      <div className="p-4">
        <div className="col-span-1 rounded-md" style={{background: "white", minHeight: "25rem"}}>
          {loadingLeadQuality && (
            <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
              <div className="loader"></div>
              <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
            </div>
          )}
          {!loadingLeadQuality && leadQualityData && leadQualityData.length > 0 && leadQualityData.length < 10 && (
            <PieChart2
              onSegmentClick={handleLeadQualityClick}
              pieData={leadQualityData}
              width="w-full"
              colors={colors}
              title="Lead Quality"
              handleDownload={handleDownload}
            />
          )}
          {!loadingLeadQuality && leadQualityData && leadQualityData.length >= 10 && (
            <div className="overflow-x-auto pb-3 px-3" style={{height: "25rem", overflowY: "auto", position: "relative"}}>
              <div className="flex justify-between items-center sticky top-0 left-0 w-full bg-white">
                <div className="text-xl font-semibold ml-3 mt-3 mb-3">Lead Quality</div>
                <div><Download className="text-gray-500 hover:text-gray-700 cursor-pointer mr-3 mt-3 mb-3" size={20} onClick={()=>handleDownload(leadQualityData, 'lead_quality')} /></div>
              </div>
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {leadQualityData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{item['name']}</td>
                      <td onClick={()=>handleLeadQualityClick(item)} className="px-4 py-3 text-sm text-gray-900 cursor-pointer text-indigo-500 hover:text-indigo-700 underline">{item['value']}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loadingLeadQuality && (!leadQualityData || leadQualityData.length === 0) && (
            <div className="flex justify-center items-center text-sm text-gray-500" style={{ minHeight: "25rem" }}>
              No lead quality data available.
            </div>
          )}
        </div>
      </div>
    );
  const CustomModuleChartsGrid = () => {
    if (!hasConfiguredValues(customModuleCharts)) {
      return renderTabLoadingOrEmpty(loadingCustomModule, "No custom module data available.");
    }
    // Tag-grouped format (object): { "TagName": { "ParamName": [...], ... }, ... }
    if (customModuleCharts && typeof customModuleCharts === 'object' && !Array.isArray(customModuleCharts)) {
      return (
        <div className="p-4">
          {Object.entries(customModuleCharts).map(([category, charts]) => (
            <div key={category} className="mb-6">
              <div className="text-xl font-semibold mb-3">{category}</div>
              <div className="grid grid-cols-3 gap-4">
                {Object.entries(charts).map(([title, data], index) => (
                  <div key={index} className="col-span-1 rounded-md" style={{background: "white", minHeight: "25rem"}}>
                    {loadingCustomModule && (
                      <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                        <div className="loader"></div>
                        <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                      </div>
                    )}
                    {!loadingCustomModule && Array.isArray(data) && data.length < 10 && (
                      <PieChart2
                        onSegmentClick={(item) => handleSegmentClick({ ...item, __parameterKey: title })}
                        pieData={data}
                        width="w-full"
                        colors={colors}
                        title={title}
                        handleDownload={handleDownload}
                      />
                    )}
                    {!loadingCustomModule && Array.isArray(data) && data.length >= 10 && (
                      <div className="overflow-x-auto px-3" style={{height: "28rem", overflowY: "auto"}}>
                        <div className="flex justify-between items-center sticky top-0 left-0 w-full bg-white">
                          <div className="text-xl font-semibold ml-3 mt-3 mb-3">{title}</div>
                          <div><Download className="text-gray-500 hover:text-gray-700 cursor-pointer mr-3 mt-3 mb-3" size={20} onClick={()=>handleDownload(data, title)} /></div>
                        </div>
                        <table className="min-w-full">
                          <thead>
                            <tr className="bg-gray-50 text-left">
                              <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                              <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {data.map((item, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-900">{item.name}</td>
                                <td onClick={()=>handleSegmentClick({ ...item, __parameterKey: title })} className="px-4 py-3 text-sm text-gray-900 cursor-pointer text-indigo-500 hover:text-indigo-700 underline">{item.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }
    // Legacy flat array format
    return (
      <div className="p-4">
        <div className="grid grid-cols-3 gap-4">
          {(Array.isArray(customModuleCharts) ? customModuleCharts : []).map((chart, index) => (
            <div key={index} className="col-span-1 rounded-md" style={{background: "white", minHeight: "25rem"}}>
              {loadingCustomModule && (
                <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                  <div className="loader"></div>
                  <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                </div>
              )}
              {!loadingCustomModule && Array.isArray(chart.data) && chart.data.length < 10 && (
                <PieChart2
                  onSegmentClick={(item) => handleSegmentClick({ ...item, __parameterKey: chart.title })}
                  pieData={chart.data}
                  width="w-full"
                  colors={colors}
                  title={chart.title}
                  handleDownload={handleDownload}
                />
              )}
              {!loadingCustomModule && Array.isArray(chart.data) && chart.data.length >= 10 && (
                <div className="overflow-x-auto px-3" style={{height: "28rem", overflowY: "auto"}}>
                  <div className="flex justify-between items-center sticky top-0 left-0 w-full bg-white">
                    <div className="text-xl font-semibold ml-3 mt-3 mb-3">{chart.title}</div>
                    <div><Download className="text-gray-500 hover:text-gray-700 cursor-pointer mr-3 mt-3 mb-3" size={20} onClick={()=>handleDownload(chart.data, chart.title)} /></div>
                  </div>
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-gray-50 text-left">
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {chart.data.map((item, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">{item.name}</td>
                          <td onClick={()=>handleSegmentClick({ ...item, __parameterKey: chart.title })} className="px-4 py-3 text-sm text-gray-900 cursor-pointer text-indigo-500 hover:text-indigo-700 underline">{item.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

    // Get available values for selected business insight parameter
    const getBusinessInsightValues = (selectedParameter) => {
      if (!selectedParameter || !dummyAdvancedFilterData.business_insight_parameters) return [];
      
      // If business_insights_parameters is an object with key-value pairs
      if (typeof dummyAdvancedFilterData.business_insight_parameters === 'object' && !Array.isArray(dummyAdvancedFilterData.business_insight_parameters)) {
        // Convert display name back to key format
        const keyFormat = selectedParameter.toLowerCase().replace(/ /g, '_');
        return dummyAdvancedFilterData.business_insight_parameters[keyFormat] || [];
      }
      
      // If business_insights_parameters is an array of arrays like [['key', ['values']], ...]
      if (Array.isArray(dummyAdvancedFilterData.business_insight_parameters)) {
        const selectedInsight = dummyAdvancedFilterData.business_insight_parameters.find(
          ([key]) => key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) === selectedParameter
        );
        return selectedInsight ? selectedInsight[1] : [];
      }
      
      return [];
    };
  
    // Get business insight parameter keys (formatted for display)
    const getBusinessInsightKeys = () => {
      if (!dummyAdvancedFilterData.business_insight_parameters) return [];
      
      // If it's an object, get the keys
      if (typeof dummyAdvancedFilterData.business_insight_parameters === 'object' && !Array.isArray(dummyAdvancedFilterData.business_insight_parameters)) {
        return Object.keys(dummyAdvancedFilterData.business_insight_parameters).map(key => 
          key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        );
      }
      
      // If it's an array of arrays, get the first element of each sub-array
      if (Array.isArray(dummyAdvancedFilterData.business_insight_parameters)) {
        return dummyAdvancedFilterData.business_insight_parameters.map(([key]) => 
          key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        );
      }
      
      return [];
    };
    const moduleParameterQueryFilters = buildParameterQueryFilters(buildModuleParameterFilters(filters, advancedFilters));

    return (
        <div className="py-3 px-5 font-sans bg-gray-50">
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Customer Insights</h1>
            <div className="text-sm text-gray-500">Analyze Insights across Process & Customers</div>
            {/* Active Filters */}
            {advancedFilters.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm p-4 mb-4 mt-4">
                <div className="flex flex-wrap gap-2">
                    {advancedFilters.map((filter, index) => (
                    <div key={index} className="flex items-center bg-gray-100 rounded-full px-3 py-1 text-sm">
                        <span className="mr-1">{filter.type}:</span>
                        <span className="font-medium">{filter.value}</span>
                        <button 
                        className="ml-2 text-gray-500 hover:text-gray-700"
                        >
                        <FaTimes className="h-3 w-3" />
                        </button>
                    </div>
                    ))}
                    {advancedFilters.length > 0 && (
                    <button 
                        className="text-sm text-blue-600 hover:text-blue-800"
                        onClick={() => setAdvancedFilters([])}
                    >
                        Clear all
                    </button>
                    )}
                </div>
                </div>
            )}
            {/* Primary Filter Section */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-4 mt-4">
                <div className="flex justify-between items-center mb-4">
                <div className="text-sm font-medium text-gray-700">Quick Filters</div>
                <div className="flex space-x-3">
                <button 
                    onClick={toggleAdvancedFilterPanel}
                    className="flex items-center text-sm text-indigo-600 hover:text-indigo-800"
                >
                    <FaFilter className="h-4 w-4 mr-1" />
                    Show Advanced Filters
                </button>
                </div>
                </div>
                <div className="grid grid-cols-5 gap-4">
              
                    <input maxLength={500} value={advancedFilters.id} type="text" placeholder="Customer ID" onChange={(e) => handleAdvancedInputChange(e, 'id')} 
                    className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none global-input" />
                
                <ModernTimeDropdown placeholder="Team" value={filters.team_name} onChange={(e) => handleFilterChange(e, 'team_name')} options={dummyAdvancedFilterData.teams} isMulti={isMultiSelectField('team_name')}/>
                <ModernTimeDropdown isDisabled={role === 'employee'} placeholder="Employee" value={filters.employee} onChange={(e) => handleFilterChange(e, 'employee')} options={dummyAdvancedFilterData.employees} isMulti={isMultiSelectField('employee')}/>
                <ModernTimeDropdown placeholder="First Interaction Time" value={filters.firstinteraction} onChange={(e) => handleFilterChange(e, 'firstinteraction')} options={dummyFilterData.firstinteraction} isMulti={isMultiSelectField('firstinteraction')}/>
                <ModernTimeDropdown placeholder="Last Interaction Time" value={filters.lastinteraction} onChange={(e) => handleFilterChange(e, 'lastinteraction')} options={dummyFilterData.lastinteraction} isMulti={isMultiSelectField('lastinteraction')}/>
                </div>
                <div>
                {filters.lastinteraction === "Custom Range" && (
                      <div className="mb-2 mt-2">
                        <ModernDateRangePicker
                          startDate={filters['Last Interaction Start']}
                          endDate={filters['Last Interaction End']}
                          rangeType={filters.lastinteraction_range_type || "between"}
                          maxRangeDays={90}
                          placeholder="Select Last Interaction Range"
                          onChange={({ startDate, endDate, rangeType }) =>
                            setFilters((prev) => ({
                              ...prev,
                              'Last Interaction Start': startDate,
                              'Last Interaction End': endDate,
                              lastinteraction_range_type: rangeType || "between",
                            }))
                          }
                        />
                      </div>
                    )}
                    {filters.firstinteraction === "Custom Range" && (
                      <div className="mb-2 mt-2">
                        <ModernDateRangePicker
                          startDate={filters['First Interaction Start']}
                          endDate={filters['First Interaction End']}
                          rangeType={filters.firstinteraction_range_type || "between"}
                          maxRangeDays={90}
                          placeholder="Select First Interaction Range"
                          onChange={({ startDate, endDate, rangeType }) =>
                            setFilters((prev) => ({
                              ...prev,
                              'First Interaction Start': startDate,
                              'First Interaction End': endDate,
                              firstinteraction_range_type: rangeType || "between",
                            }))
                          }
                        />
                      </div>
                    )}
                </div>
                {/* Filter Display */}
                <div className="flex gap-4 mb-4 mt-4 flex-wrap">
                    {Object.keys(filters).map(
                      (key) =>{
                        if (
                          key === 'First Interaction Start' ||
                          key === 'First Interaction End' ||
                          key === 'Last Interaction Start' ||
                          key === 'Last Interaction End' ||
                          key === 'firstinteraction_range_type' ||
                          key === 'lastinteraction_range_type' ||
                          key === 'time' ||
                          key === 'customStart' ||
                          key === 'customEnd'
                        ) {
                          return null;
                        }
                        if (key === 'firstinteraction' && filters.firstinteraction === 'Custom Range') {
                          return null;
                        }
                        if (key === 'lastinteraction' && filters.lastinteraction === 'Custom Range') {
                          return null;
                        }
                        return (Array.isArray(filters[key]) ? filters[key].length > 0 : filters[key]) && (
                          <div key={key} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                            <span className="capitalize text-indigo-600">{key} - {Array.isArray(filters[key]) ? (filters[key].length > 2 ? `${filters[key].slice(0, 2).join(', ')} +${filters[key].length - 2} more` : filters[key].join(', ')) : filters[key]}</span>
                            {(key!=='lastinteraction' && !(role === 'employee' && key === 'employee')) && <button
                              className="ml-2 text-gray-600"
                              onClick={() => setFilters((prev) => ({ ...prev, [key]: '' }))}
                            >
                              <FaTimes />
                            </button>}
                          </div>
                        )
                      })}
                      {filters.firstinteraction === 'Custom Range' && filters['First Interaction Start'] && (filters.firstinteraction_range_type === "after" || filters['First Interaction End']) && (
                        <div className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                          <span className="text-indigo-600">
                            First Interaction - {filters['First Interaction Start']} {filters.firstinteraction_range_type === "after" ? "onwards" : `to ${filters['First Interaction End']}`}
                          </span>
                          <button
                            className="ml-2 text-gray-600"
                            onClick={() =>
                              setFilters((prev) => ({
                                ...prev,
                                firstinteraction: '',
                                firstinteraction_range_type: 'between',
                                'First Interaction Start': '',
                                'First Interaction End': '',
                              }))
                            }
                          >
                            <FaTimes />
                          </button>
                        </div>
                      )}
                      {filters.lastinteraction === 'Custom Range' && filters['Last Interaction Start'] && (filters.lastinteraction_range_type === "after" || filters['Last Interaction End']) && (
                        <div className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                          <span className="text-indigo-600">
                            Last Interaction - {filters['Last Interaction Start']} {filters.lastinteraction_range_type === "after" ? "onwards" : `to ${filters['Last Interaction End']}`}
                          </span>
                          <button
                            className="ml-2 text-gray-600"
                            onClick={() =>
                              setFilters((prev) => ({
                                ...prev,
                                lastinteraction: '',
                                lastinteraction_range_type: 'between',
                                'Last Interaction Start': '',
                                'Last Interaction End': '',
                              }))
                            }
                          >
                            <FaTimes />
                          </button>
                        </div>
                      )}
                      {/* Show advanced filters that are set (excluding defaults) */}
                      {Object.entries(moduleParameterQueryFilters).map(([key, val]) => {
                        if (filters[key] !== undefined) return null;
                        if (key.endsWith('_lower') || key.endsWith('_upper')) return null;
                        return (
                          <div key={key} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                            <span className="capitalize text-indigo-600">{key} - {String(val)}</span>
                            <button
                              className="ml-2 text-gray-600"
                              onClick={() => clearModuleFilterByBaseKey(key)}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        );
                      })}
                      {Array.from(
                        new Set(
                          Object.keys(moduleParameterQueryFilters)
                            .filter((key) => key.endsWith('_lower') || key.endsWith('_upper'))
                            .map((key) => key.replace(/_(lower|upper)$/, ''))
                        )
                      ).map((baseKey) => {
                        const lower = moduleParameterQueryFilters[`${baseKey}_lower`];
                        const upper = moduleParameterQueryFilters[`${baseKey}_upper`];
                        if (lower === undefined && upper === undefined) return null;
                        return (
                          <div key={`${baseKey}-range`} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                            <span className="capitalize text-indigo-600">{baseKey} - {String(lower ?? '')} to {String(upper ?? '')}</span>
                            <button
                              className="ml-2 text-gray-600"
                              onClick={() => clearModuleFilterByBaseKey(baseKey)}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        );
                      })}
                      {Object.entries(advancedFilters).map(([key, val]) => {
                        // skip if blank or default
                        if (
                          val === ''
                        ) {
                          return null;
                        }

                        if (key === 'business_insights_multiple' || key === 'custom_module_multiple' || key === 'custom_module_parameter_name' || key === 'custom_module_parameter_answer') {
                          return null;
                        }
                        if (shouldHideCrmLeadCreatedAtChipKey(key)) {
                          return null;
                        }
                        if(key !== 'business_insights_multiple'){
                        return (
                          <div key={key} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                            <span className="capitalize text-indigo-600">{key} - {String(val)}</span>
                            <button
                              className="ml-2 text-gray-600"
                              onClick={() => setAdvancedFilters((prev) => ({ ...prev, [key]: '' }))}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        );
                        }
                      })}
                      <CrmLeadCreatedAtFilterChips
                        advancedFilters={advancedFilters}
                        setAdvancedFilters={setAdvancedFilters}
                      />
                    </div>
            </div>

            {/* Advanced Filter Panel (appears from the right) */}
            {showAdvancedFilterPanel && (
              <CustomerAdvancedFiltersPanel
                isOpen={showAdvancedFilterPanel}
                onClose={toggleAdvancedFilterPanel}
                multiSelectFields={multiSelectFields}
                advancedFilters={advancedFilters}
                setAdvancedFilters={setAdvancedFilters}
                handleAdvancedInputChange={handleAdvancedInputChange}
                dummyAdvancedFilterData={dummyAdvancedFilterData}
                expandedSections={expandedSections}
                setExpandedSections={setExpandedSections}
                updateBusinessInsights={updateBusinessInsights}
                getBusinessInsightKeys={getBusinessInsightKeys}
                getBusinessInsightValues={getBusinessInsightValues}
                handleConcernCategoryChange={handleConcernCategoryChange}
                getConcernCategoryKeys={getConcernCategoryKeys}
                getConcernSubcategoriesForCategory={getConcernSubcategoriesForCategory}
                handleQueryCategoryChange={handleQueryCategoryChange}
                getQueryCategoryKeys={getQueryCategoryKeys}
                getQuerySubcategoriesForCategory={getQuerySubcategoriesForCategory}
                handleCustomModuleParameterChange={handleCustomModuleParameterChange}
                getCustomModuleKeys={getCustomModuleKeys}
                getCustomModuleValues={getCustomModuleValues}
                handleDispositionChange={handleDispositionChange}
                getDispositionKeys={getDispositionKeys}
                getSubdispositionsForDisposition={getSubdispositionsForDisposition}
                crmStageKey="crm_lead_stage"
                crmSubStageKey="crm_lead_sub_stage"
                showDispositionSection={true}
                showCrmLeadCreatedAt={true}
              />
            )}
            {/* Analytics Tabs */}
            {firstAvailableAnalyticsTab && (
            <div className="bg-white rounded-md mt-4">
              <div className="px-4 pt-3 flex flex-wrap items-center gap-4 border-b">
                {showCustomModuleTab && (
                  <button
                    className={`px-3 py-2 text-sm whitespace-nowrap ${selectedAnalyticsTab === 'custom_module' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-600'}`}
                    onClick={() => setSelectedAnalyticsTab('custom_module')}
                  >
                    Custom Module
                  </button>
                )}
                {showBusinessInsightsTab && (
                  <button
                    className={`px-3 py-2 text-sm whitespace-nowrap ${selectedAnalyticsTab === 'business' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-600'}`}
                    onClick={() => setSelectedAnalyticsTab('business')}
                  >
                    Business Insights
                  </button>
                )}
                {showFirstResponseTimeTab && (
                  <button
                    className={`px-3 py-2 text-sm whitespace-nowrap ${selectedAnalyticsTab === 'first_response_time' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-600'}`}
                    onClick={() => setSelectedAnalyticsTab('first_response_time')}
                  >
                    First Response Time
                  </button>
                )}
                {showConversationDispositionTab && (
                  <button
                    className={`px-3 py-2 text-sm whitespace-nowrap ${selectedAnalyticsTab === 'conversation_disposition' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-600'}`}
                    onClick={() => setSelectedAnalyticsTab('conversation_disposition')}
                  >
                    Dispositions & Sub-Dispositions
                  </button>
                )}
                {showLeadQualityTab && (
                  <button
                    className={`px-3 py-2 text-sm whitespace-nowrap ${selectedAnalyticsTab === 'lead_quality' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-600'}`}
                    onClick={() => setSelectedAnalyticsTab('lead_quality')}
                  >
                    Lead Quality
                  </button>
                )}
              </div>
              {selectedAnalyticsTab === 'business' && showBusinessInsightsTab && <PieChartsGrid/>}
              {selectedAnalyticsTab === 'custom_module' && showCustomModuleTab && <CustomModuleChartsGrid/>}
              {selectedAnalyticsTab === 'first_response_time' && showFirstResponseTimeTab && renderFirstResponseTimeCharts()}
              {selectedAnalyticsTab === 'conversation_disposition' && showConversationDispositionTab && renderConversationDispositionCharts()}
              {selectedAnalyticsTab === 'lead_quality' && showLeadQualityTab && renderLeadQualityCharts()}
            </div>
            )}
            {
                isModalOpen && (
                    <div style={{position: "fixed", left: "0%", top: "0%", width: "100%", height: "100%", background: "rgba(0, 0, 0, 0.5)", zIndex: "1001"}}>
                        <div style={{position: "fixed", left: "2%", top: "3.5%", width: "96%", height: "93%", background: "white", borderRadius: "10px", boxShadow: "rgba(0, 0, 0, 0.05) 0px 1px 3px", 
                        zIndex: "999999999", padding: "5px"}}>
                            <div style={{position: "absolute", right: "10px", top: "10px", cursor: "pointer"}} onClick={()=>setIsModalOpen(false)}><FaTimes style={{color: "slategray"}}/></div>
                            <div style={{overflow: "auto", height: "100%"}}>
                              <CustomerRecords segment={selectedSegment} serviceID={service_id} type="modal-open" clientID={client_id}/>
                            </div>
                        </div>
                    </div>
                )
            }
            <style jsx>
            {`
            .sections-headings {
                            cursor: pointer;
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            padding: 15px;
                            border-radius: 10px;
                            transition: background 0.3s;
                            width: 100%;
                            }

                            .section-title {
                            font-size: 15px;
                            font-weight: 500;
                            display: flex;
                            align-items: center;
                            }
            `}              
          </style>
        </div>
    )
}

export default BusinessInsights;
